import { ENDPOINTS, useActions } from 'hammock';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DefaultItemProps, Select, SelectProps } from '@candu-ai/analytics/ui';
import { SEGMENT_DEFAULT_ID } from '@candu-ai/segments/types';

import { useWhitelistedSegmentIds } from './state/selectors';

interface AutocompleteFromHammockGetAllProps<Item, Data, Values>
  extends Omit<SelectProps<Item, Values>, 'options'> {
  getFormattedResults: (results: Data, searchValue: string) => Item[];
  onQuerySuccess?: (data: Data) => void;
  endpoint: ENDPOINTS;
  isSegment?: boolean;
}

export const AutocompleteFromHammockGetAll = <Item extends DefaultItemProps, Data, Values>({
  getFormattedResults,
  onQuerySuccess,
  selectFirstByDefault = false,
  name,
  isMultiselect,
  endpoint,
  isSegment = false,
  ...props
}: AutocompleteFromHammockGetAllProps<Item, Data, Values>) => {
  const { getAll } = useActions(endpoint);
  const [data, setData] = useState<any>([]);
  const [fetchingResults, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>('');
  const whitelistedSegmentIds = useWhitelistedSegmentIds();

  const loadData = async () => {
    setLoading(true);

    const results = (await getAll()) as any;
    setLoading(false);

    if (!isSegment) {
      setData(results);
      return;
    }

    const allSegments = [
      {
        id: SEGMENT_DEFAULT_ID,
        name: SEGMENT_DEFAULT_ID,
      },
      ...(results || []),
    ];
    if (!whitelistedSegmentIds || whitelistedSegmentIds.length === 0) {
      setData(allSegments);
      return;
    }

    setData(
      allSegments
        .filter((s) => whitelistedSegmentIds.includes(s.id))
        .sort((a, b) => whitelistedSegmentIds.indexOf(a.id) - whitelistedSegmentIds.indexOf(b.id)),
    );
  };

  const items: Item[] = useMemo(() => {
    if (!data) {
      return [];
    }
    return getFormattedResults(data, searchValue);
  }, [data, getFormattedResults, searchValue]);

  const updateSearchValue = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const clearSearchValue = useCallback(() => {
    setSearchValue('');
  }, []);

  useEffect(() => {
    loadData();
  }, [endpoint]);

  if (!items.length) {
    return null;
  }

  return (
    <Select<Item, Values>
      {...props}
      name={name}
      loading={fetchingResults}
      onSearch={updateSearchValue}
      onClearSearch={clearSearchValue}
      searchValue={searchValue}
      options={items}
      selectFirstByDefault={selectFirstByDefault}
      isMultiselect={isMultiselect}
    />
  );
};
